import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Sun,
  Moon,
  Trophy,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/**
 * RulesPage - Gulf Coast Hackers League Rules & Guidelines
 *
 * Design Philosophy:
 * - Matches the modern card-based design of Leaderboard/Events/Quotas pages
 * - Numbered and lettered rules for easy reference (e.g., "Rule 1.c")
 * - Dark mode support
 * - Clear visual hierarchy
 */
const RulesPage = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const location = useLocation();

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else {
      try {
        const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (mediaQuery?.matches) {
          setIsDarkMode(true);
        }
      } catch {
        // Fallback for test environments
      }
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);

  // Rule Number Badge Component
  const RuleNumber = ({ number, size = "large" }) => (
    <span className={`
      flex-shrink-0 rounded-full bg-red-600 text-white font-bold flex items-center justify-center
      ${size === "large" ? "w-8 h-8 text-base" : "w-6 h-6 text-sm"}
    `}>
      {number}
    </span>
  );

  // Sub-rule Letter Badge Component
  const SubRuleLetter = ({ letter }) => (
    <span className="flex-shrink-0 w-6 h-6 rounded bg-blue-600 text-white font-bold text-sm flex items-center justify-center">
      {letter}
    </span>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Rules & Guidelines
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    The Game, Player Conduct and the Rules
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="flex flex-wrap gap-2">
              <a
                href="#general-rules"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-forest-50 dark:bg-forest-900/20 text-forest-700 dark:text-forest-400 hover:bg-forest-100 dark:hover:bg-forest-900/40 transition-colors text-sm font-medium"
              >
                General Rules
              </a>
              <a
                href="#fedup-cup"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gold-50 dark:bg-gold-900/20 text-gold-700 dark:text-gold-400 hover:bg-gold-100 dark:hover:bg-gold-900/40 transition-colors text-sm font-medium"
              >
                <Trophy className="w-4 h-4" />
                FedUp Cup
              </a>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* General League Rules Section */}
          <section id="general-rules" className="mb-12">
            <h2 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-6">
              General League Rules
            </h2>

            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 shadow-lg rounded-xl p-6 md:p-8">

              {/* Rule 1: Local Rules */}
              <div className="mb-8" id="rule-1">
                <div className="flex items-start gap-3 mb-4">
                  <RuleNumber number="1" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Local Rules</h3>
                </div>

                <div className="ml-11 space-y-4">
                  {/* 1.a */}
                  <div className="flex items-start gap-3" id="rule-1a">
                    <SubRuleLetter letter="a" />
                    <p className="text-gray-700 dark:text-gray-300">
                      Unless stated below, we follow the{" "}
                      <a
                        href="https://www.usga.org/content/usga/home-page/rules/rules-2019/rules-of-golf/rules-and-definitions.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                      >
                        USGA Rules of Golf
                        <ExternalLink className="w-3 h-3" />
                      </a>.
                    </p>
                  </div>

                  {/* 1.b */}
                  <div className="flex items-start gap-3" id="rule-1b">
                    <SubRuleLetter letter="b" />
                    <p className="text-gray-700 dark:text-gray-300">
                      A player is allowed a <strong>"mercy mulligan"</strong> on the first non-par 3 hole they play.
                      If they choose to use it, they must play the mulligan.
                      Once the player leaves the tee box, the "mercy mulligan" is forfeited.
                    </p>
                  </div>

                  {/* 1.c */}
                  <div className="flex items-start gap-3" id="rule-1c">
                    <SubRuleLetter letter="c" />
                    <p className="text-gray-700 dark:text-gray-300">
                      Play the ball as it lies, using common sense (e.g., you don't have to hit from a tree root or a divot in the fairway).
                    </p>
                  </div>

                  {/* 1.d */}
                  <div className="flex items-start gap-3" id="rule-1d">
                    <SubRuleLetter letter="d" />
                    <p className="text-gray-700 dark:text-gray-300">
                      A player may, with a <strong>1-stroke penalty</strong>, place the ball anywhere along its original line of flight (not closer to the green or pin),
                      including the fairway, in cases of out of bounds, a lost ball, or an unplayable lie.
                    </p>
                  </div>

                  {/* 1.e */}
                  <div className="flex items-start gap-3" id="rule-1e">
                    <SubRuleLetter letter="e" />
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="font-medium">All balls must be putted out. No Gimmies.</span>
                    </div>
                  </div>

                  {/* 1.f */}
                  <div className="flex items-start gap-3" id="rule-1f">
                    <SubRuleLetter letter="f" />
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Lost Ball:</strong> If a player hits a ball that is "clearly in play" and their playing partners agree it should not be lost,
                      the player may place a new ball where it is believed to have been, without penalty.
                      This rule applies only in the general area and not in a penalty area.
                    </p>
                  </div>

                  {/* 1.g */}
                  <div className="flex items-start gap-3" id="rule-1g">
                    <SubRuleLetter letter="g" />
                    <p className="text-gray-700 dark:text-gray-300">
                      If a player accidentally moves their ball while addressing it on the green, they may replace it without penalty.{" "}
                      <a
                        href="https://www.usga.org/content/usga/home-page/rules-hub/2017-local-rule/local-rule--accidental-movement-of-a-ball-on-the-putting-green.html#expanded"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                      >
                        Follows USGA Local Rule
                        <ExternalLink className="w-3 h-3" />
                      </a>.
                    </p>
                  </div>

                  {/* 1.h */}
                  <div className="flex items-start gap-3" id="rule-1h">
                    <SubRuleLetter letter="h" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-3">
                        A player may, <strong>without penalty</strong>, lift and place the ball (no closer to the green or pin) in the same area (fairway, rough, bunker, etc.)
                        under the following conditions, as agreed upon by the group:
                      </p>
                      <div className="grid gap-2 ml-1">
                        {[
                          { title: "Roots", desc: "The ball lies in a spot where the player risks hitting roots during the back or forward swing. Relief must not provide an unfair advantage. Note: Tree trunks or branches are not considered roots." },
                          { title: "Cleaning or Divots", desc: "Cleaning is appropriate, or the ball lies in a divot or Ground Under Repair (GUR), marked or unmarked, in the fairway. Wet Days: Cleaning is allowed anywhere on the course if all players agree before the round." },
                          { title: "Bunker", desc: "The ball lies in an unnatural hole (e.g., shoe print or divot), standing water, or mud in a bunker." },
                          { title: "Man-Made Obstacles", desc: "The ball lies in or near a man-made obstacle such as a sprinkler head, cart path, drainage rocks/grate/pipes, or electrical boxes." },
                          { title: "Unnatural Obstacles", desc: "The ball lies in or near an obstacle that shouldn't be on the course (e.g., fallen or cut trees/branches, grass cuttings, raked leaves, alligators, or nesting birds)." },
                          { title: "Putting Surface", desc: "The ball lies on a green with spots that are dead, covered with sand, or affected by an old pin hole on the putting line. The ball may be moved a maximum of one putter's length, no closer to the pin." },
                        ].map((item, index) => (
                          <div key={index} className="flex items-start gap-2 p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                            <CheckCircle className="w-4 h-4 text-forest-500 dark:text-forest-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="font-semibold text-gray-900 dark:text-white">{item.title}:</span>{" "}
                              <span className="text-gray-600 dark:text-gray-400">{item.desc}</span>
                            </div>
                          </div>
                        ))}
                        <div className="flex items-start gap-2 p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                          <CheckCircle className="w-4 h-4 text-forest-500 dark:text-forest-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="font-semibold text-gray-900 dark:text-white">Embedded Ball:</span>{" "}
                            <span className="text-gray-600 dark:text-gray-400">
                              The ball is embedded anywhere in the general area, but not in a penalty area.{" "}
                              <a
                                href="https://www.usga.org/content/usga/home-page/rules/rules-2019/players-edition/rule-16.html"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                              >
                                Follows USGA Rule 16.3
                                <ExternalLink className="w-3 h-3" />
                              </a>.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rule 2: Playing the Round */}
              <div className="mb-8" id="rule-2">
                <div className="flex items-start gap-3 mb-4">
                  <RuleNumber number="2" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Playing the Round</h3>
                </div>

                <div className="ml-11 space-y-4">
                  {/* 2.a */}
                  <div className="flex items-start gap-3" id="rule-2a">
                    <SubRuleLetter letter="a" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-2">All will play from the same tee box except:</p>
                      <ul className="list-disc ml-6 space-y-1 text-gray-600 dark:text-gray-400">
                        <li>Age 65+ and quota &lt; 26 play from one tee box forward except on par 3s (see below).</li>
                        <li>Quota &lt; 6 play one tee box forward except on par 3s (see below).</li>
                        <li>Players with quotas 26 - 30 play one tee box back.</li>
                        <li>Players with a 31+ quota play from the tips. If we are playing from the tips, tee up someplace beyond the tips.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 2.b */}
                  <div className="flex items-start gap-3" id="rule-2b">
                    <SubRuleLetter letter="b" />
                    <p className="text-gray-700 dark:text-gray-300">
                      On <strong>Par 3s</strong> all play from the same tee except if the hole is &gt;180 yards (measured to the flag with rangefinder) then
                      upfront tee players play the upfront tee.
                    </p>
                  </div>

                  {/* 2.c */}
                  <div className="flex items-start gap-3" id="rule-2c">
                    <SubRuleLetter letter="c" />
                    <p className="text-gray-700 dark:text-gray-300">
                      The rules committee will consider requests from players under the age of 65 with a disability to play from the forward tees.
                      If approved, they must adhere to the rules applicable to players under 65.
                    </p>
                  </div>
                </div>
              </div>

              {/* Rule 3: Competition */}
              <div className="mb-8" id="rule-3">
                <div className="flex items-start gap-3 mb-4">
                  <RuleNumber number="3" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Competition</h3>
                </div>

                <div className="ml-11 space-y-4">
                  {/* 3.a */}
                  <div className="flex items-start gap-3" id="rule-3a">
                    <SubRuleLetter letter="a" />
                    <p className="text-gray-700 dark:text-gray-300">
                      Must have at least a <strong>4-some</strong> to have Stableford scores count on non-Sunday events (does not count for FedUp Cup points).
                    </p>
                  </div>

                  {/* 3.b */}
                  <div className="flex items-start gap-3" id="rule-3b">
                    <SubRuleLetter letter="b" />
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>Greenies (CTPs)</strong> - If you are the closest to the pin you must either birdie
                      or par the hole to win. Failure to do so will result in a carryover to the next par 3.
                      If there is no next par 3, we are all winners and $1 will be returned to each player.
                    </p>
                  </div>

                  {/* 3.c */}
                  <div className="flex items-start gap-3" id="rule-3c">
                    <SubRuleLetter letter="c" />
                    <p className="text-gray-700 dark:text-gray-300">
                      New players will be <strong>capped at +2</strong> for the Stableford competition for their first two money game rounds.
                    </p>
                  </div>

                  {/* 3.d */}
                  <div className="flex items-start gap-3" id="rule-3d">
                    <SubRuleLetter letter="d" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        <strong>$24 buy-ins</strong> (must be all in):
                      </p>
                      <ul className="list-disc ml-6 space-y-1 text-gray-600 dark:text-gray-400">
                        <li>$10 for Stableford (quota game).</li>
                        <li>$10 for skins.</li>
                        <li>$4 for greenies (CTPs).</li>
                        <li>If more par 3's, increase amount.</li>
                        <li>
                          <button
                            onClick={() => setIsPayoutModalOpen(true)}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Payout for Stableford
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* 3.e */}
                  <div className="flex items-start gap-3" id="rule-3e">
                    <SubRuleLetter letter="e" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Finishing the round:</strong></p>
                      <ul className="list-disc ml-6 space-y-1 text-gray-600 dark:text-gray-400">
                        <li>If you are not able to finish a round due to an injury or weather conditions (all must quit) your score is not counted, and you don't have to pay for the money games.</li>
                        <li>If a player fails to complete their round (other than Injury/Sickness/Weather), they are expected to pay for the money games. Their scorecard is counted towards Stableford, and they are eligible for the Stableford, Greenies and Skins games.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 3.f */}
                  <div className="flex items-start gap-3" id="rule-3f">
                    <SubRuleLetter letter="f" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Strokes in skins game based on Stableford quota:</strong></p>
                      <ul className="list-disc ml-6 space-y-1 text-gray-600 dark:text-gray-400">
                        <li>1-5 get one stroke on the two hardest handicap holes per 9</li>
                        <li>6-9 get one stroke per 9 on the hardest handicap holes</li>
                        <li>&gt;= 10 gets 0 strokes.</li>
                      </ul>
                    </div>
                  </div>

                  {/* 3.g */}
                  <div className="flex items-start gap-3" id="rule-3g">
                    <SubRuleLetter letter="g" />
                    <div className="flex-1">
                      <p className="text-gray-700 dark:text-gray-300 mb-2"><strong>Pairings:</strong></p>
                      <ul className="list-disc ml-6 space-y-1 text-gray-600 dark:text-gray-400">
                        <li>If we have four groups or less, we will draw cards at the course.</li>
                        <li>If we have five groups or more, someone will pre-draw the pairing so those in the last group or second last group can show up a little later, but you must be committed to showing up.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rule 4: Player Conduct */}
              <div id="rule-4">
                <div className="flex items-start gap-3 mb-4">
                  <RuleNumber number="4" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Player Conduct</h3>
                </div>

                <div className="ml-11 space-y-4">
                  {/* 4.a */}
                  <div className="flex items-start gap-3" id="rule-4a">
                    <SubRuleLetter letter="a" />
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-semibold">
                      <AlertCircle className="w-4 h-4" />
                      Don't be a dick.
                    </div>
                  </div>

                  {/* 4.b */}
                  <div className="flex items-start gap-3" id="rule-4b">
                    <SubRuleLetter letter="b" />
                    <p className="text-gray-700 dark:text-gray-300">
                      If you can't make it, <strong>cancel ASAP</strong> so someone else can play
                      or we can reduce tee times and not get yelled at from the course.
                    </p>
                  </div>

                  {/* 4.c */}
                  <div className="flex items-start gap-3" id="rule-4c">
                    <SubRuleLetter letter="c" />
                    <p className="text-gray-700 dark:text-gray-300">
                      When you have a double bogey, take your medicine and <strong>pick up your ball</strong>.
                    </p>
                  </div>

                  {/* 4.d */}
                  <div className="flex items-start gap-3" id="rule-4d">
                    <SubRuleLetter letter="d" />
                    <p className="text-gray-700 dark:text-gray-300">
                      Unless you have an approved medical condition, must partake in birdie shots.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* FedUp Cup Section */}
          <section id="fedup-cup">
            <h2 className="text-2xl font-bold text-gold-600 dark:text-gold-400 mb-6 flex items-center gap-3">
              <Trophy className="w-7 h-7" />
              FedUp Cup
            </h2>

            {/* Overview Cards */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-4 text-center shadow-lg">
                <div className="text-3xl font-bold text-gold-600 dark:text-gold-400">$50</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Season Buy-in</div>
              </div>
              <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-forest-600 dark:text-forest-400">January 12th</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Season Start</div>
              </div>
              <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">Sunday Only</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">FedUp Events</div>
              </div>
            </div>

            {/* Point Structure */}
            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-6 shadow-lg mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Point Structure</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Finish Points */}
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Finishes (Net Score)</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { place: "1st", points: 100 },
                      { place: "2nd", points: 90 },
                      { place: "3rd", points: 80 },
                      { place: "4th", points: 70 },
                      { place: "5th", points: 60 },
                      { place: "6th-12th", points: "50-10" },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                        <span className="text-gray-600 dark:text-gray-400">{item.place}</span>
                        <span className="font-bold text-forest-600 dark:text-forest-400">{item.points} pts</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bonus Points */}
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">Bonus Points</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                      <span className="text-gray-600 dark:text-gray-400">Participation</span>
                      <span className="font-bold text-gold-600 dark:text-gold-400">5 pts</span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                      <span className="text-gray-600 dark:text-gray-400">Skins (per skin)</span>
                      <span className="font-bold text-gold-600 dark:text-gold-400">2 pts</span>
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-navy-800/50">
                      <span className="text-gray-600 dark:text-gray-400">CTP (per CTP)</span>
                      <span className="font-bold text-gold-600 dark:text-gold-400">3 pts</span>
                    </div>
                  </div>
                  <div className="mt-3 p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm">
                    <strong>Max 10 points</strong> combined CTP + Skin points per week
                  </div>
                </div>
              </div>
            </div>

            {/* Three Majors */}
            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-6 shadow-lg mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Three "Majors"</h3>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center border border-emerald-200 dark:border-emerald-800">
                  <div className="font-bold text-emerald-700 dark:text-emerald-400">Spring</div>
                  <div className="text-sm text-emerald-600 dark:text-emerald-500">April</div>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center border border-amber-200 dark:border-amber-800">
                  <div className="font-bold text-amber-700 dark:text-amber-400">Summer</div>
                  <div className="text-sm text-amber-600 dark:text-amber-500">July</div>
                </div>
                <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-center border border-orange-200 dark:border-orange-800">
                  <div className="font-bold text-orange-700 dark:text-orange-400">Fall</div>
                  <div className="text-sm text-orange-600 dark:text-orange-500">October</div>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gold-50 dark:bg-gold-900/20 border border-gold-200 dark:border-gold-800">
                <p className="font-semibold text-gold-800 dark:text-gold-300 mb-2">Points are 1.5x regular weeks:</p>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1 text-gold-700 dark:text-gold-400">
                    <div>1st: <strong>150 pts</strong></div>
                    <div>2nd: <strong>135 pts</strong></div>
                    <div>3rd: <strong>120 pts</strong></div>
                  </div>
                  <div className="space-y-1 text-gold-700 dark:text-gold-400">
                    <div>Participation: <strong>8 pts</strong></div>
                    <div>Skins: <strong>3 pts</strong>, CTP: <strong>5 pts</strong></div>
                    <div>Max CTPs/Skins: <strong>15 pts</strong></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Playoffs */}
            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-6 shadow-lg mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Playoffs</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Championship Bracket */}
                <div className="p-4 rounded-lg bg-forest-50 dark:bg-forest-900/20 border border-forest-200 dark:border-forest-800">
                  <h4 className="font-bold text-forest-800 dark:text-forest-300 mb-3 flex items-center gap-2">
                    <Trophy className="w-5 h-5" />
                    Championship Bracket
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="p-2 rounded bg-white dark:bg-navy-900/50">
                      <div className="font-semibold text-forest-700 dark:text-forest-400">Week 1 – Dec 6th</div>
                      <p className="text-gray-600 dark:text-gray-400">Top 8 point leaders + ties qualify. Points reset to 100, 90, 80...30.</p>
                    </div>
                    <div className="p-2 rounded bg-white dark:bg-navy-900/50">
                      <div className="font-semibold text-forest-700 dark:text-forest-400">Week 2 – Dec 13th</div>
                      <p className="text-gray-600 dark:text-gray-400">Top 4 + ties advance. 1st place doubles to 200 points.</p>
                    </div>
                    <div className="p-2 rounded bg-white dark:bg-navy-900/50">
                      <div className="font-semibold text-forest-700 dark:text-forest-400">Tie Breaker</div>
                      <p className="text-gray-600 dark:text-gray-400">Highest regular season rank wins. Still tied? 20ft putt off.</p>
                    </div>
                  </div>
                </div>

                {/* Loser's Bracket */}
                <div className="p-4 rounded-lg bg-gray-100 dark:bg-navy-800/50 border border-gray-200 dark:border-navy-700">
                  <h4 className="font-bold text-gray-800 dark:text-gray-300 mb-3">Loser's Bracket</h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm">Most over quota combined during playoff weeks</p>
                  <div className="space-y-2">
                    {[
                      { place: "1st", amount: "$30" },
                      { place: "2nd", amount: "$20" },
                      { place: "3rd", amount: "$10" },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded bg-white dark:bg-navy-900/50">
                        <span className="text-gray-600 dark:text-gray-400">{item.place}</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">{item.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Final Payouts */}
            <div className="bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800 rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Championship Payouts</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">Funded by $50 entry fees</p>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { place: "1st", pct: "50%" },
                  { place: "2nd", pct: "20%" },
                  { place: "3rd", pct: "15%" },
                  { place: "4th", pct: "10%" },
                  { place: "5th", pct: "5%" },
                ].map((item, index) => (
                  <div key={index} className={`p-3 rounded-lg text-center ${
                    index === 0
                      ? 'bg-gold-100 dark:bg-gold-900/30 border-2 border-gold-400 dark:border-gold-600'
                      : 'bg-gray-50 dark:bg-navy-800/50'
                  }`}>
                    <div className={`font-bold ${
                      index === 0 ? 'text-gold-700 dark:text-gold-400' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {item.place}
                    </div>
                    <div className={`text-xl font-bold ${
                      index === 0 ? 'text-gold-600 dark:text-gold-500' : 'text-forest-600 dark:text-forest-400'
                    }`}>
                      {item.pct}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Stableford Payout Modal */}
      {isPayoutModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setIsPayoutModalOpen(false)}
        >
          <div
            className="relative bg-white dark:bg-navy-900 rounded-xl p-4 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 p-1 rounded-full bg-gray-100 dark:bg-navy-800 hover:bg-gray-200 dark:hover:bg-navy-700 text-gray-600 dark:text-gray-400"
              onClick={() => setIsPayoutModalOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Stableford Payout Chart</h3>
            <img
              src="/assets/payouts.png"
              alt="Stableford Payout"
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default RulesPage;
